import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../api/services/authService";
import "./LoginPage.css";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailMasked, setEmailMasked] = useState("");
  const navigate = useNavigate();

  const startResendCooldown = () => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleStep1 = async (e) => {
    e.preventDefault();
    setError("");
    setAttemptsLeft(null);
    if (!email.trim() || !password.trim()) {
      setError("Please enter email and password.");
      return;
    }
    setLoading(true);
    const res = await AuthService.authStep1({ email: email.trim(), password });
    setLoading(false);
    if (res?.success) {
      setEmailMasked(res.data?.email_masked || "");
      setStep(2);
      setAttemptsLeft(null);
      startResendCooldown();
    } else {
      setError(res?.message || "Invalid email or password.");
      setAttemptsLeft(res?.data?.attempts_left ?? null);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError("");
    setAttemptsLeft(null);
    if (!otp.trim()) {
      setError("Please enter the OTP sent to your email.");
      return;
    }
    setLoading(true);
    const res = await AuthService.authStep2({
      email: email.trim(),
      password,
      otp: otp.trim(),
    });
    setLoading(false);
    if (res?.success) {
      const { token, admin } = res.data;
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("userName", admin?.first_name || email.split("@")[0]);
      sessionStorage.setItem("userType", String(admin?.admin_type ?? 1));
      sessionStorage.setItem("admin", JSON.stringify(admin));
      sessionStorage.setItem("permissions", JSON.stringify(admin?.permissions || []));
      navigate("/dashboard/homepage", { replace: true });
    } else {
      setError(res?.message || "Invalid OTP. Please try again.");
      setAttemptsLeft(res?.data?.attempts_left ?? null);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    const res = await AuthService.resendOtp(email.trim());
    setLoading(false);
    if (res?.success) {
      startResendCooldown();
    } else {
      setError(res?.message || "Failed to resend OTP.");
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp("");
    setError("");
    setAttemptsLeft(null);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card-header">
          <img src="/favicon_light.svg" alt="Logo" className="login-logo" />
          <h1>Wrathcode Exchange Admin Panel</h1>
          <p>{step === 1 ? "Sign in to manage your exchange" : "Enter the OTP sent to your email"}</p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1} className="login-form">
            {error && (
              <div className="login-error">
                {error}
                {attemptsLeft != null && (
                  <span className="login-attempts"> {attemptsLeft} attempts remaining.</span>
                )}
              </div>
            )}
            <label className="login-label">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                autoComplete="email"
                className="login-input"
              />
            </label>
            <label className="login-label">
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="login-input"
              />
            </label>
            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? "Sending OTP…" : "Continue"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleStep2} className="login-form">
            {error && (
              <div className="login-error">
                {error}
                {attemptsLeft != null && (
                  <span className="login-attempts"> {attemptsLeft} attempts remaining.</span>
                )}
              </div>
            )}
            {emailMasked && (
              <p className="login-otp-hint">OTP sent to {emailMasked}</p>
            )}
            <label className="login-label">
              <span>OTP</span>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                autoComplete="one-time-code"
                className="login-input"
                maxLength={6}
                inputMode="numeric"
              />
            </label>
            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? "Verifying…" : "Sign in"}
            </button>
            <div className="login-otp-actions">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendCooldown > 0 || loading}
                className="login-resend"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
              </button>
              <button type="button" onClick={handleBack} className="login-back">
                Back
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
